import { createSelector, createSlice } from "@reduxjs/toolkit"
import { RootState } from "integrations/redux"
import { EventCategory } from "@nova/schemas"
import {
    ActivityFilters,
    CategorySelection,
    DateFilter,
    defaultActivityFilters,
    emptyCategorySelection,
    isValidDateFilter,
} from "./activityFilters"

type ActivitySlice = {
    search: string
    staged: ActivityFilters
    applied: ActivityFilters
}

const initialState: ActivitySlice = {
    search: "",
    staged: defaultActivityFilters,
    applied: defaultActivityFilters,
}

export const activitySliceName = "activity" as const

export const {
    actions: {
        setSearch,
        setStagedDate,
        setStagedPeople,
        setStagedCategory,
        clearStagedCategory,
        applyStagedFilters,
        discardStagedFilters,
        clearStagedFilters,
        removeAppliedPeople,
        removeAppliedCategory,
    },
    reducer: ActivitySliceReducer,
} = createSlice({
    name: activitySliceName,
    initialState,
    reducers: {
        setSearch: (state, action: { payload: string }) => {
            state.search = action.payload
        },
        setStagedDate: (state, action: { payload: DateFilter }) => {
            if (!isValidDateFilter(action.payload)) return
            state.staged.date = action.payload
        },
        setStagedPeople: (state, action: { payload: string[] }) => {
            state.staged.people = action.payload
        },
        setStagedCategory: (
            state,
            action: { payload: { category: EventCategory; selection: CategorySelection } }
        ) => {
            state.staged.categories[action.payload.category] = action.payload.selection
        },
        clearStagedCategory: (state, action: { payload: EventCategory }) => {
            delete state.staged.categories[action.payload]
        },
        applyStagedFilters: (state) => {
            state.applied = state.staged
        },
        discardStagedFilters: (state) => {
            state.staged = state.applied
        },
        clearStagedFilters: (state) => {
            state.staged = defaultActivityFilters
        },
        removeAppliedPeople: (state) => {
            state.applied.people = []
            state.staged.people = []
        },
        removeAppliedCategory: (state, action: { payload: EventCategory }) => {
            delete state.applied.categories[action.payload]
            delete state.staged.categories[action.payload]
        },
    },
})

const root = (state: RootState) => state[activitySliceName]

export const selectActivitySearch = createSelector([root], (s) => s.search)
export const selectStagedFilters = createSelector([root], (s) => s.staged)
export const selectAppliedFilters = createSelector([root], (s) => s.applied)
export const selectStagedCategory = (category: EventCategory) =>
    createSelector([root], (s) => s.staged.categories[category] ?? emptyCategorySelection)
